import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, RefreshCw, ShieldCheck, UserPlus } from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import api, { formatApiError } from "@/lib/api";
import PermissionGovernance from "@/pages/dashboard/PermissionGovernance";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "support", label: "Soporte técnico" },
  { value: "director", label: "Director de respaldo" },
];
const roleLabel = (role) => ROLES.find((item) => item.value === role)?.label || role;

const TrustedTeamManagement = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "admin" });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const responses = await Promise.all(
        ROLES.map(({ value }) => api.get("/api/admin/users", { params: { role: value, limit: 500 } }))
      );
      const unique = new Map();
      responses.flatMap(({ data }) => data).forEach((row) => unique.set(row.id, row));
      setRows([...unique.values()].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy("create"); setError(""); setOk("");
    try {
      await api.post("/api/director/trusted-team", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        password: form.password,
        role: form.role,
      });
      setOk("Cuenta permanente creada. El acceso queda activo inmediatamente.");
      setForm({ name: "", email: "", phone: "", password: "", role: "admin" });
      await load();
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setBusy("");
    }
  };

  const changeRole = async (row, role) => {
    if ((row.roles || []).includes(role)) return;
    setBusy(row.id); setError(""); setOk("");
    try {
      await api.put(`/api/director/trusted-team/${row.id}/role`, { role });
      setOk(`${row.email} ahora tiene el cargo ${roleLabel(role)}.`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const stats = useMemo(() => Object.fromEntries(
    ROLES.map(({ value }) => [value, rows.filter((r) => (r.roles || []).includes(value)).length])
  ), [rows]);

  return (
    <PageShell
      kicker="Director KD9 · Gobierno de acceso"
      title="EQUIPO DE CONFIANZA"
      desc="Crea las cuentas de autoridad y gobierna permisos individuales permanentes o temporales. La matriz base continúa siendo la autoridad inicial del servidor."
      badge={<button type="button" onClick={load} className="kd9-btn-outline p-2 rounded-lg" aria-label="Actualizar"><RefreshCw size={15} /></button>}
      testId="trusted-team-page"
    >
      {error && <div className="mb-4 p-3 rounded-lg border border-red-800 text-red-300">{error}</div>}
      {ok && <div className="mb-4 p-3 rounded-lg border border-emerald-700 text-emerald-300">{ok}</div>}

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {ROLES.map((item) => (
          <div key={item.value} className="kd9-card p-4">
            <div className="text-2xl font-cinzel kd9-gold-text">{stats[item.value] || 0}</div>
            <div className="text-[10px] uppercase tracking-widest">{item.label}</div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="kd9-card p-5 mb-7">
        <div className="flex items-center gap-2 mb-4"><UserPlus size={17} className="kd9-gold-text" /><strong>Crear cuenta permanente</strong></div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-xs">Nombre completo
            <input className="kd9-input w-full mt-1 p-3" required minLength={2} maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="text-xs">Correo
            <input className="kd9-input w-full mt-1 p-3" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label className="text-xs">Teléfono (opcional)
            <input className="kd9-input w-full mt-1 p-3" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <label className="text-xs">Cargo
            <select className="kd9-input w-full mt-1 p-3" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="text-xs md:col-span-2">Contraseña permanente
            <div className="relative mt-1">
              <input className="kd9-input w-full p-3 pr-12" type={showPassword ? "text" : "password"} required minLength={8} maxLength={128} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>{showPassword ? <EyeOff size={17} /> : <Eye size={17} />}</button>
            </div>
          </label>
        </div>
        <p className="text-[11px] mt-3" style={{ color: "var(--kd9-text-soft)" }}>La contraseña se cifra y nunca vuelve a mostrarse. Compártela por un canal seguro.</p>
        <button disabled={busy === "create"} className="kd9-btn-primary mt-4 px-6 py-3 rounded-lg">{busy === "create" ? "Creando…" : "Crear y activar cuenta"}</button>
      </form>

      <div className="kd9-card overflow-hidden">
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: "var(--kd9-border)" }}><ShieldCheck size={16} className="kd9-gold-text" /><strong>Accesos actuales</strong></div>
        {rows.length === 0 ? <div className="p-8 text-center">Aún no hay cuentas privilegiadas.</div> : rows.map((row) => {
          const current = ROLES.find(({ value }) => (row.roles || []).includes(value))?.value || "admin";
          return (
            <div key={row.id} className="p-4 border-b last:border-0 grid md:grid-cols-[1fr_220px_auto] gap-3 items-center" style={{ borderColor: "var(--kd9-border)" }}>
              <div><strong>{row.name}</strong><div className="text-xs">{row.email}</div></div>
              <select className="kd9-input p-2" value={current} disabled={busy === row.id} onChange={(e) => changeRole(row, e.target.value)}>
                {ROLES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
              </select>
              <span className="text-[10px] uppercase tracking-widest">{row.status}</span>
            </div>
          );
        })}
      </div>

      <PermissionGovernance />
    </PageShell>
  );
};

export default TrustedTeamManagement;

